import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { EMPTY_MASTER_DATA } from './prItemTemplate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Loads every master-data list the PR Create/Edit forms need.
 * Returns `{ masterData, loadingMasterData, orgDefaults }`.
 * `orgDefaults` carries the first purchasing-org / company-code so the
 * Create page can seed header defaults (Edit ignores it).
 */
export default function usePRMasterData() {
  const [masterData, setMasterData] = useState(EMPTY_MASTER_DATA);
  const [loadingMasterData, setLoadingMasterData] = useState(true);
  const [orgDefaults, setOrgDefaults] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingMasterData(true);
        const [
          docTypesRes, itemCategoriesRes, accountAssignmentRes, processingStatusRes,
          releaseIndicatorRes, materialGroupsRes, purchOrgRes, purchGroupRes,
          companyRes, plantRes, slocRes, materialRes, ccRes, glRes, vendorRes,
          coAreaRes, profitCenterRes, valuationTypesRes, mrpControllersRes, uomRes,
        ] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/p2p/document-types/`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/sap/item-categories`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/sap/account-assignment-categories`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/sap/processing-statuses`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/sap/release-indicators`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/sap/material-groups`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/purchasing-management/purchasing-organizations`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/purchasing-management/purchasing-groups`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/company/company-codes`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/material-management/plants`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/material-management/storage-locations`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/materials`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/controlling/cost-centers`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/sap/gl-accounts`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/vendor-master/vendors`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/controlling/controlling-areas`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/controlling/profit-centers`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/valuation-types`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/mrp-controllers`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/units-of-measure`).catch(() => ({ data: [] })),
        ]);

        const mappedDocTypes = docTypesRes.data.length > 0
          ? docTypesRes.data.map((dt) => ({ document_type_id: dt.document_type_id, description: dt.description }))
          : [{ document_type_id: 'NB', description: 'Standard Purchase Requisition' }];
        // De-dupe by document_type_id (API can return repeats → React key warning)
        const uniqueDocTypes = Array.from(
          new Map(mappedDocTypes.map((dt) => [dt.document_type_id, dt])).values()
        );

        setMasterData((prev) => ({
          ...prev,
          documentTypes: uniqueDocTypes,
          itemCategories: itemCategoriesRes.data,
          accountAssignmentCategories: accountAssignmentRes.data,
          processingStatuses: processingStatusRes.data,
          releaseIndicators: releaseIndicatorRes.data,
          materialGroups: materialGroupsRes.data,
          purchasingOrgs: purchOrgRes.data,
          purchasingGroups: purchGroupRes.data,
          companyCodes: companyRes.data,
          plants: plantRes.data,
          storageLocations: slocRes.data,
          materials: materialRes.data,
          costCenters: ccRes.data,
          glAccounts: glRes.data,
          vendors: vendorRes.data,
          controllingAreas: coAreaRes.data,
          profitCenters: profitCenterRes.data,
          valuationTypes: valuationTypesRes.data.length > 0 ? valuationTypesRes.data : ['NEW', 'USED', 'IMPORT', 'EXPORT'],
          mrpControllers: mrpControllersRes.data,
          unitsOfMeasure: uomRes.data.length > 0
            ? uomRes.data
            : [{ unit_code: 'EA', description: 'Each' }, { unit_code: 'PC', description: 'Piece' }, { unit_code: 'KG', description: 'Kilogram' }],
        }));

        setOrgDefaults({
          purchasing_organization: purchOrgRes.data[0]?.purchasing_org_id,
          company_code: companyRes.data[0]?.company_code_id,
        });
      } catch (error) {
        console.error('Error loading master data:', error);
        toast.error('Error loading master data');
      } finally {
        setLoadingMasterData(false);
      }
    };
    load();
  }, []);

  return { masterData, loadingMasterData, orgDefaults };
}
